import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc, onSnapshot, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { Shop } from '../types';

// 統合ユーザーシステムの型定義
export interface ShopAccess {
  shopId: string;
  role: 'owner' | 'manager' | 'staff';
  grantedAt: Date;
  grantedBy: string;
}

export interface UnifiedUser {
  id: string;
  email: string;
  displayName: string;
  phoneNumber?: string;
  photoURL?: string;
  systemRole: 'platformAdmin' | 'business' | 'customer';
  organizationId?: string;
  shopAccess: ShopAccess[];
  isActive: boolean;
  isSuspended: boolean;
  createdAt: Date;
  updatedAt: Date;
  lastLoginAt?: Date;
}

interface AuthContextType {
  user: User | null;
  unifiedUser: UnifiedUser | null;
  shop: Shop | null;
  currentShopId: string | null;
  currentRole: 'owner' | 'manager' | 'staff' | null;
  availableShops: ShopAccess[];
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  switchShop: (shopId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [unifiedUser, setUnifiedUser] = useState<UnifiedUser | null>(null);
  const [shop, setShop] = useState<Shop | null>(null);
  const [currentShopId, setCurrentShopId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribeUser: (() => void) | null = null;
    let unsubscribeShop: (() => void) | null = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      // 既存のリスナーをクリーンアップ
      if (unsubscribeUser) {
        unsubscribeUser();
        unsubscribeUser = null;
      }
      if (unsubscribeShop) {
        unsubscribeShop();
        unsubscribeShop = null;
      }

      if (firebaseUser) {
        try {
          console.log('Fetching unified user for:', firebaseUser.uid);

          let legacyChecked = false; // 後方互換性チェック済みフラグ

          // 統合ユーザー情報を取得（リアルタイムリスナー）
          unsubscribeUser = onSnapshot(
            doc(db, 'users', firebaseUser.uid),
            async (userDoc) => {
              if (userDoc.exists()) {
                const userData = userDoc.data();
                const unifiedUserData: UnifiedUser = {
                  id: userDoc.id,
                  email: userData.email,
                  displayName: userData.displayName,
                  phoneNumber: userData.phoneNumber,
                  photoURL: userData.photoURL,
                  systemRole: userData.systemRole || 'business',
                  organizationId: userData.organizationId,
                  shopAccess: (userData.shopAccess || []).map((access: any) => ({
                    ...access,
                    grantedAt: access.grantedAt?.toDate?.() || new Date(),
                  })),
                  isActive: userData.isActive ?? true,
                  isSuspended: userData.isSuspended ?? false,
                  createdAt: userData.createdAt?.toDate?.() || new Date(),
                  updatedAt: userData.updatedAt?.toDate?.() || new Date(),
                  lastLoginAt: userData.lastLoginAt?.toDate?.(),
                };

                setUnifiedUser(unifiedUserData);

                // shopAccessがある場合、店舗情報を取得
                if (unifiedUserData.shopAccess.length > 0) {
                  // 保存された選択店舗を取得、なければ最初の店舗
                  const savedShopId = localStorage.getItem('selectedShopId');
                  const targetShopId =
                    savedShopId &&
                    unifiedUserData.shopAccess.some(
                      (access) => access.shopId === savedShopId
                    )
                      ? savedShopId
                      : unifiedUserData.shopAccess[0].shopId;

                  setCurrentShopId(targetShopId);

                  // 店舗情報をリアルタイムで取得
                  if (unsubscribeShop) {
                    unsubscribeShop();
                  }
                  unsubscribeShop = onSnapshot(
                    doc(db, 'shops', targetShopId),
                    (shopDoc) => {
                      if (shopDoc.exists()) {
                        const shopData = shopDoc.data();
                        const fullShop = {
                          id: shopDoc.id,
                          ...shopData,
                          createdAt: shopData.createdAt?.toDate?.() || new Date(),
                          updatedAt: shopData.updatedAt?.toDate?.() || new Date(),
                        } as Shop;
                        setShop(fullShop);
                        setLoading(false);
                      } else {
                        console.warn('Shop document not found:', targetShopId);
                        setShop(null);
                        setLoading(false);
                      }
                    }
                  );
                } else {
                  console.warn('User has no shop access:', firebaseUser.uid);
                  setShop(null);
                  setCurrentShopId(null);
                  setLoading(false);
                }
              } else {
                // usersコレクションにデータがない場合、旧システムとの互換性チェック（一度だけ）
                if (!legacyChecked) {
                  legacyChecked = true;
                  console.warn(
                    'User not found in unified system, checking legacy shops...'
                  );

                  // 旧システムとの互換性：shopsコレクションをチェック
                  // 1. shopId = userIdのケースをチェック
                  let legacyShopDoc: any = await getDoc(doc(db, 'shops', firebaseUser.uid));
                  let legacyShopId = firebaseUser.uid;
                  let shopExists = legacyShopDoc.exists();

                  // 2. ownerIdで検索
                  if (!shopExists) {
                    console.log('Checking shops by ownerId...');
                    const shopsQuery = await getDocs(
                      query(
                        collection(db, 'shops'),
                        where('ownerId', '==', firebaseUser.uid)
                      )
                    );
                    if (!shopsQuery.empty) {
                      legacyShopDoc = shopsQuery.docs[0];
                      legacyShopId = legacyShopDoc.id;
                      shopExists = true;
                    }
                  }

                  if (shopExists) {
                    console.log('Found legacy shop, user needs migration');
                    const shopData = legacyShopDoc.data();
                    if (!shopData) {
                      console.error('Legacy shop document exists but has no data');
                      setUnifiedUser(null);
                      setShop(null);
                      setCurrentShopId(null);
                      setLoading(false);
                      return;
                    }

                    // 旧システムのデータを一時的に使用
                    setUnifiedUser({
                      id: firebaseUser.uid,
                      email: firebaseUser.email || '',
                      displayName: firebaseUser.displayName || '',
                      systemRole: 'business',
                      shopAccess: [{
                        shopId: legacyShopId,
                        role: 'owner',
                        grantedAt: new Date(),
                        grantedBy: firebaseUser.uid,
                      }],
                      isActive: true,
                      isSuspended: false,
                      createdAt: new Date(),
                      updatedAt: new Date(),
                    });

                    setCurrentShopId(legacyShopId);

                    // 店舗情報をリアルタイムで監視（保存後の反映に必要）
                    if (unsubscribeShop) {
                      unsubscribeShop();
                    }
                    unsubscribeShop = onSnapshot(
                      doc(db, 'shops', legacyShopId),
                      (shopDoc) => {
                        if (shopDoc.exists()) {
                          const latestShopData = shopDoc.data();
                          setShop({
                            id: shopDoc.id,
                            ...latestShopData,
                            createdAt: latestShopData.createdAt?.toDate?.() || new Date(),
                            updatedAt: latestShopData.updatedAt?.toDate?.() || new Date(),
                          } as Shop);
                        }
                        setLoading(false);
                      }
                    );
                  } else {
                  setUnifiedUser(null);
                  setShop(null);
                  setCurrentShopId(null);
                  setLoading(false);
                }
                } else {
                  // 既にチェック済みの場合はローディングを終了
                  setLoading(false);
                }
              }
            },
            (error) => {
              console.error('Error fetching unified user:', error);
              setUnifiedUser(null);
              setShop(null);
              setLoading(false);
            }
          );
        } catch (error) {
          console.error('Error setting up user listener:', error);
          setUnifiedUser(null);
          setShop(null);
          setLoading(false);
        }
      } else {
        setUnifiedUser(null);
        setShop(null);
        setCurrentShopId(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeUser) {
        unsubscribeUser();
      }
      if (unsubscribeShop) {
        unsubscribeShop();
      }
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error('ログインに失敗しました。メールアドレスとパスワードを確認してください。');
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setShop(null);
      setUnifiedUser(null);
      setCurrentShopId(null);
      localStorage.removeItem('selectedShopId');
    } catch (error) {
      console.error('Logout error:', error);
      throw new Error('ログアウトに失敗しました');
    }
  };

  const switchShop = async (shopId: string) => {
    if (!unifiedUser) {
      throw new Error('User not authenticated');
    }

    const hasAccess = unifiedUser.shopAccess.some(
      (access) => access.shopId === shopId
    );

    if (!hasAccess) {
      throw new Error('You do not have access to this shop');
    }

    try {
      const shopDoc = await getDoc(doc(db, 'shops', shopId));
      if (shopDoc.exists()) {
        const shopData = shopDoc.data();
        const fullShop = {
          id: shopDoc.id,
          ...shopData,
          createdAt: shopData.createdAt?.toDate?.() || new Date(),
          updatedAt: shopData.updatedAt?.toDate?.() || new Date(),
        } as Shop;
        setShop(fullShop);
        setCurrentShopId(shopId);
        localStorage.setItem('selectedShopId', shopId);
      } else {
        throw new Error('Shop not found');
      }
    } catch (error) {
      console.error('Error switching shop:', error);
      throw error;
    }
  };

  const currentRole =
    currentShopId && unifiedUser
      ? unifiedUser.shopAccess.find((access) => access.shopId === currentShopId)
          ?.role || null
      : null;

  const availableShops = unifiedUser?.shopAccess || [];

  const value = {
    user,
    unifiedUser,
    shop,
    currentShopId,
    currentRole,
    availableShops,
    loading,
    signIn,
    signOut,
    switchShop,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
